/**
 * Per-client-TYPE editorial field PACKS (media-model.md §3). The CORE spine lives in
 * typed columns; the PACK is the small set of editorial fields that differ by client
 * type, stored in media_groups.metadata (jsonb — add fields with no migration). The
 * build (M4) reads these onto the Work index / project pages.
 *
 * v1 is the PHOTOGRAPHY pack, designed against Fred Hardy's structure (Work index →
 * project pages: fred-hardy-build-plan.md §1). Deliberately focused, NOT a generic
 * schema — refine it against Fred's real content. (The videography pack stays the
 * Cooper content/films.ts shape; we add it here when the first video client lands.)
 */
export type PackFieldType = 'text' | 'textarea';

export interface PackField {
  key: string; // the metadata jsonb key
  label: string; // designer-facing label
  type: PackFieldType;
  placeholder?: string;
  help?: string;
}

/**
 * Photography project pack v1 (project-level — these describe a series/project, not a
 * single frame). Per-frame caption + alt live on the media row itself (M3a), not here.
 */
export const PHOTOGRAPHY_PROJECT_PACK: PackField[] = [
  { key: 'subtitle', label: 'Subtitle / series', type: 'text', placeholder: 'e.g. A coastal series' },
  { key: 'location', label: 'Location', type: 'text', placeholder: 'e.g. Pembrokeshire, Wales' },
  { key: 'year', label: 'Date / year', type: 'text', placeholder: 'e.g. 2025' },
  { key: 'client', label: 'Client (if commissioned)', type: 'text', placeholder: 'e.g. Kinfolk' },
  { key: 'credits', label: 'Credits', type: 'text', placeholder: 'e.g. Styling — Jane Doe' },
  { key: 'description', label: 'Short description', type: 'textarea', placeholder: 'A sentence or two about the work.' },
];

/**
 * The pack for a group, by its kind. Galleries are lighter than projects (a gallery is
 * often just a themed set), so they take the same fields minus the commission specifics
 * — keep it simple for v1; both are photography here.
 */
export function packForKind(kind: 'gallery' | 'project'): PackField[] {
  if (kind === 'project') return PHOTOGRAPHY_PROJECT_PACK;
  return PHOTOGRAPHY_PROJECT_PACK.filter((f) => ['subtitle', 'location', 'description'].includes(f.key));
}
